import { getRaasContext, setRaasContext } from "./context";
import { ensureGoogleCsaLoaded } from "./google-csa";
import {
  getGoogCsa,
  isNonEmptyString,
  pickRandomUnique,
  uuidV4,
} from "./helpers";
import { sdkError, sdkLog } from "./log";
import { replaceRaasCsaAndDrain, installRaasCsaStub } from "./raas-csa";
import { parseInitFromCurrentScript } from "./script-init";
import { wireAdClickListener } from "./ad-click";
import type { AdClickedCallback, RaasInitConfig } from "./types";
import { createEventsClient } from "./events";

let raasCsaWired = false;
let adClickListenerWired = false;

const forwardToGoogCsa = async (
  command: string,
  page: unknown,
  blocks: unknown[],
): Promise<void> => {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const { config } = getRaasContext();

  await ensureGoogleCsaLoaded();

  const goog = getGoogCsa();
  if (typeof goog !== "function") {
    sdkError("[Raas] _googCsa is not available");
    return;
  }

  let nextPage = page;
  let nextBlocks = blocks;

  if (command === "ads") {
    const serpId = uuidV4();
    const sendEvent = createEventsClient({
      tenantId: config.tenantId,
      tenantKey: config.tenantKey,
    });

    const pageObj = page as Record<string, unknown>;

    const clientChannels = String(pageObj?.channel ?? "")
      .split("+")
      .map((x) => x.trim())
      .filter(Boolean);
    const uniqueClientChannels = Array.from(new Set(clientChannels));
    const revAttChannels = pickRandomUnique(
      config.allocatedChannels.filter((c) => !uniqueClientChannels.includes(c)),
      3,
    );

    if (typeof window !== "undefined" && !adClickListenerWired) {
      adClickListenerWired = true;
      window.addEventListener(
        "message",
        wireAdClickListener(() => {
          sendEvent({
            type: "ad_click",
            serpId,
            revAttChannels,
          });
          if (typeof pageObj.acb === "function") {
            (pageObj.acb as AdClickedCallback)();
          }
        }),
      );
    }

    nextPage = {
      ...pageObj,
      channel: [...uniqueClientChannels, ...revAttChannels].join("+"),
    };

    delete (nextPage as Record<string, unknown>).acb;

    sendEvent({
      type: "search",
      serpId,
      revAttChannels,
    });

    nextBlocks = blocks.map((block) => {
      const b = block as Record<string, unknown>;
      const original = b.adLoadedCallback;
      const wrapped = (...args: unknown[]) => {
        const adsLoaded = Boolean(args[1]);
        sendEvent({
          type: adsLoaded ? "serp_ad_loaded" : "serp_ad_not_loaded",
          serpId,
          revAttChannels,
        });
        if (typeof original === "function") {
          return (original as (...a: unknown[]) => unknown)(...args);
        }
      };
      // always ensure the callback exists
      return { ...b, adLoadedCallback: wrapped };
    });
  }

  goog(command, nextPage, ...nextBlocks);
};

const wireRaasCsaOnce = (): void => {
  if (raasCsaWired) return;
  raasCsaWired = true;
  replaceRaasCsaAndDrain((command, page, ...blocks) => {
    void forwardToGoogCsa(command, page, blocks).catch((e) => sdkError(e));
  });
};

export const init = (config: RaasInitConfig): void => {
  const verbose = config.verbose ?? false;

  if (!isNonEmptyString(config.tenantId)) {
    sdkError("[Raas] init: tenantId is required", verbose);
    return;
  }
  if (!isNonEmptyString(config.tenantKey)) {
    sdkError("[Raas] init: tenantKey is required", verbose);
    return;
  }
  if (!Array.isArray(config.allocatedChannels)) {
    sdkError(
      "[Raas] init: allocatedChannels must be an array of strings",
      verbose,
    );
    return;
  }
  if (!config.allocatedChannels.every((c) => isNonEmptyString(c))) {
    sdkError(
      "[Raas] init: allocatedChannels must contain only non-empty strings",
      verbose,
    );
    return;
  }

  const normalized: RaasInitConfig = {
    ...config,
    verbose,
  };

  setRaasContext(normalized);
  wireRaasCsaOnce();
  sdkLog("init complete");

  void ensureGoogleCsaLoaded().catch((err) => {
    sdkError(err);
  });
};

installRaasCsaStub(window);
const autoInit = parseInitFromCurrentScript();
if (autoInit) init(autoInit);
